// SolmanS3Button.jsx — tombol "Upload to S3" per roadmap/project di halaman Solman Documents.
// Backend: backend/solmanS3.js (/api/solman-docs/s3/*)
import React, { useCallback, useEffect, useState } from 'react';
import { UploadCloud, Loader2, StopCircle, CheckCircle2, AlertTriangle, HardDrive } from 'lucide-react';

const hdr = (json) => {
  const h = { Authorization: 'Bearer ' + (localStorage.getItem('auth_token') || '') };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};
async function call(url, body) {
  const r = await fetch(url, body ? { method: 'POST', headers: hdr(true), body: JSON.stringify(body) } : { headers: hdr(false) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
  return j;
}

export default function SolmanS3Button({ strucId }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!strucId) return;
    try { setSt(await call('/api/solman-docs/s3/status?strucId=' + encodeURIComponent(strucId))); }
    catch (e) { setErr(e.message); }
  }, [strucId]);

  useEffect(() => { setSt(null); setErr(''); load(); }, [load]);
  const job = st && st.job;
  const mine = job && job.struc_id === strucId;
  const running = !!(job && job.running);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const s = st && st.struc;
  if (!s || !s.fetched) return null;

  const start = async (force) => {
    if (force && !window.confirm('Re-upload ALL fetched documents of this project to S3 (overwrite)?')) return;
    setBusy(true); setErr('');
    try { await call('/api/solman-docs/s3/upload', { strucId, force: !!force }); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const local = async (kind) => {
    const mb = (s.freeable_bytes / 1048576).toFixed(1);
    const msg = kind === 'free'
      ? `Delete ${s.freeable} local PDF file(s) (${mb} MB) of this project from the server?\n\nEach file is deleted only after its S3 copy is verified identical (size + MD5). OCR text stays searchable; documents are opened directly from S3.`
      : `Download ${s.s3_only} PDF file(s) of this project back from S3 to the server?`;
    if (!window.confirm(msg)) return;
    setBusy(true); setErr('');
    try { await call('/api/solman-docs/s3/' + kind, { strucId }); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const cancel = async () => { try { await call('/api/solman-docs/s3/cancel', {}); await load(); } catch (e) { setErr(e.message); } };

  const allUp = s.ocr_complete && s.uploaded >= s.fetched && !s.failed;
  const where = `s3://${st.bucket}/${s.s3_prefix}`;
  const title = !s.ocr_complete
    ? `Available once OCR of this project is complete (${s.ocr_pending} PDF waiting for OCR, ${s.pending_fetch} not fetched yet).`
    : `Upload fetched PDFs + OCR text to ${where} (same hierarchy as on this page). Each local PDF is deleted right after its upload is verified (MD5); documents are then opened directly from S3. Already uploaded, unchanged files are skipped.` +
      (st.auto ? ' Auto-upload is on: runs by itself after OCR finishes.' : '');

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {mine && running ? (
        <>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-sky-300 text-sky-700 bg-sky-50">
            <Loader2 size={14} className="animate-spin" />
            {job.kind === 'restore' ? 'Restoring' : job.kind === 'free' ? 'Freeing' : 'S3 upload'} {job.done}/{job.total}
            {job.failed ? <span className="text-rose-600"> · {job.failed} failed</span> : null}
          </span>
          <button type="button" onClick={cancel}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50">
            <StopCircle size={14} /> Stop
          </button>
        </>
      ) : (
        <button type="button" disabled={busy || running || !s.ocr_complete} onClick={(e) => start(e.shiftKey)} title={title + ' (Shift+click = force re-upload all)'}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-sky-300 text-sky-700 hover:bg-sky-50 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
          {allUp ? 'Re-sync S3' : 'Upload to S3'}
        </button>
      )}
      {!(mine && running) && allUp && s.freeable > 0 ? (
        <button type="button" disabled={busy || running} onClick={() => local('free')}
          title="Delete local PDFs (uploaded earlier) that are verified identical in S3. Search (OCR) keeps working; documents open from S3."
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
          <HardDrive size={14} /> Free local space ({(s.freeable_bytes / 1048576).toFixed(0)} MB)
        </button>
      ) : null}
      {s.uploaded > 0 && !(mine && running) ? (
        <span className={'inline-flex items-center gap-1 text-xs ' + (s.failed ? 'text-amber-700' : 'text-emerald-700')}
          title={where + (s.last_upload ? ' · last upload ' + s.last_upload : '')}>
          {s.failed ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
          S3 {s.uploaded}/{s.fetched}{s.failed ? ` · ${s.failed} failed` : ''}
        </span>
      ) : null}
      {running && !mine ? <span className="text-xs text-slate-500">S3 upload busy: {job.name}</span> : null}
      {!running && mine && job.last_error ? <span className="text-xs text-rose-600" title={job.last_error}>Last S3 error: {job.last_error.slice(0, 80)}</span> : null}
      {err ? <span className="text-xs text-rose-600">{err}</span> : null}
    </span>
  );
}
